import React, { useState, useEffect, useMemo } from 'react';
import { X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CreatePlanModal({ onClose, onSave, lines, projects, initialDate }) {
  const [form, setForm] = useState({
    date: initialDate || new Date().toISOString().split('T')[0],
    line_id: '', project_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  // City Bus set selection
  const [selectedSets, setSelectedSets] = useState([]);
  const [plannedSets, setPlannedSets] = useState([]);
  const [setInput, setSetInput] = useState('');
  const [setInputError, setSetInputError] = useState('');

  const selectedProject = projects.find(p => p.id === form.project_id);
  const isHitachi = !!(selectedProject?.hitachi_seat_types?.length > 0);
  const isTrain = selectedProject?.planning_unit === 'seat' && !isHitachi;
  const isSetBased = selectedProject?.planning_unit === 'set' || isHitachi;
  const isCityBus = selectedProject?.category === 'City Bus';
  const orderedQty = selectedProject?.ordered_quantity || 0;
  const preCompletedSets = isHitachi ? (selectedProject?.pre_completed_sets || 0) : 0;

  useEffect(() => {
    if (isSetBased && form.project_id) {
      if (isHitachi) {
        axios.get(`${API}/api/hitachi/plans/${form.project_id}/planned-sets`)
          .then(res => setPlannedSets(res.data.planned_sets || []))
          .catch(() => {});
      } else {
        axios.get(`${API}/api/plans/citybus-planned-sets/${form.project_id}`)
          .then(res => setPlannedSets(res.data.planned_sets || []))
          .catch(() => {});
      }
    } else {
      setPlannedSets([]);
      setSelectedSets([]);
    }
  }, [form.project_id, isSetBased, isHitachi]);

  const plannedSetNums = useMemo(() => new Set(plannedSets.map(s => s.set_number)), [plannedSets]);

  // Parse input like "1, 3, 5-10, 15" into an array of numbers
  const parseSetInput = (input) => {
    const nums = [];
    const parts = input.split(',').map(s => s.trim()).filter(Boolean);
    for (const part of parts) {
      if (part.includes('-')) {
        const [startStr, endStr] = part.split('-').map(s => s.trim());
        const start = parseInt(startStr);
        const end = parseInt(endStr);
        if (isNaN(start) || isNaN(end)) return { error: `Invalid range: "${part}"` };
        if (start > end) return { error: `Invalid range: ${start} > ${end}` };
        if (end - start > 500) return { error: `Range too large: "${part}"` };
        for (let i = start; i <= end; i++) nums.push(i);
      } else {
        const n = parseInt(part);
        if (isNaN(n)) return { error: `Invalid number: "${part}"` };
        nums.push(n);
      }
    }
    return { nums: [...new Set(nums)] };
  };

  const addSetsFromInput = () => {
    if (!setInput.trim()) return;
    const result = parseSetInput(setInput);
    if (result.error) { setSetInputError(result.error); return; }
    setSetInputError('');
    const valid = result.nums.filter(n => n >= 1 && n <= orderedQty);
    const outOfRange = result.nums.filter(n => n < 1 || n > orderedQty);
    const preCompleted = valid.filter(n => n <= preCompletedSets);
    const eligible = valid.filter(n => n > preCompletedSets);
    const alreadyPlanned = eligible.filter(n => plannedSetNums.has(n));
    const alreadySelected = eligible.filter(n => selectedSets.includes(n) && !plannedSetNums.has(n));
    const newSets = eligible.filter(n => !plannedSetNums.has(n) && !selectedSets.includes(n));

    if (newSets.length > 0) {
      setSelectedSets(prev => [...prev, ...newSets].sort((a, b) => a - b));
    }
    const warnings = [];
    if (outOfRange.length) warnings.push(`Out of range (1-${orderedQty}): ${outOfRange.join(', ')}`);
    if (preCompleted.length) warnings.push(`Pre-completed (1-${preCompletedSets}): ${preCompleted.join(', ')}`);
    if (alreadyPlanned.length) warnings.push(`Already planned: ${alreadyPlanned.join(', ')}`);
    if (alreadySelected.length) warnings.push(`Already selected: ${alreadySelected.join(', ')}`);
    if (warnings.length) setSetInputError(warnings.join(' | '));
    else setSetInputError('');
    setSetInput('');
  };

  const removeSet = (num) => setSelectedSets(prev => prev.filter(n => n !== num));

  const filteredProjects = useMemo(() => {
    let result = projects;
    // Hide completed projects (all ordered sets shipped)
    result = result.filter(p => {
      const ordered = p.ordered_quantity || 0;
      const shipped = p.total_shipped || 0;
      if (ordered > 0 && shipped >= ordered) return false;
      return true;
    });
    if (catFilter === 'train') result = result.filter(p => p.planning_unit === 'seat');
    else if (catFilter === 'bus') result = result.filter(p => p.planning_unit === 'set' && (p.category || '').toLowerCase().includes('bus'));
    else if (catFilter === 'coach') result = result.filter(p => p.planning_unit === 'set' && (p.category || '').toLowerCase().includes('coach'));
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.sr_number || '').toLowerCase().includes(q) ||
        (p.customer_name || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [projects, search, catFilter]);

  useEffect(() => {
    if (selectedProject?.line_id && !form.line_id) {
      setForm(f => ({ ...f, line_id: selectedProject.line_id }));
    }
  }, [selectedProject, form.line_id]);

  // Format selected sets compactly: [1,2,3,5,7,8,9] -> "1-3, 5, 7-9"
  const formatSetRanges = (sets) => {
    if (!sets.length) return '';
    const sorted = [...sets].sort((a, b) => a - b);
    const ranges = [];
    let start = sorted[0], end = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] === end + 1) { end = sorted[i]; }
      else { ranges.push(start === end ? `${start}` : `${start}-${end}`); start = sorted[i]; end = sorted[i]; }
    }
    ranges.push(start === end ? `${start}` : `${start}-${end}`);
    return ranges.join(', ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.line_id || !form.project_id) { toast.error('Select line and project'); return; }

    // Over-planning check for set-based projects
    if (isSetBased && selectedSets.length > 0 && orderedQty > 0) {
      const totalAfter = plannedSetNums.size + selectedSets.filter(n => !plannedSetNums.has(n)).length;
      if (totalAfter > orderedQty) {
        const proceed = window.confirm(
          `You are exceeding the ordered quantity!\n\nOrdered: ${orderedQty} sets\nAlready planned: ${plannedSetNums.size}\nNew sets: ${selectedSets.filter(n => !plannedSetNums.has(n)).length}\nTotal after: ${totalAfter}\n\nDo you still want to proceed with production?`
        );
        if (!proceed) return;
      }
    }

    setLoading(true);
    try {
      if (isHitachi) {
        if (selectedSets.length === 0) { toast.error('Select at least one set'); setLoading(false); return; }
        const res = await axios.post(`${API}/api/hitachi/plans/create-sets`, {
          date: form.date, project_id: form.project_id, line_id: form.line_id,
          set_numbers: selectedSets.sort((a, b) => a - b)
        });
        toast.success(`${res.data?.created || selectedSets.length} Hitachi sets planned${res.data?.skipped?.length ? ` (${res.data.skipped.length} skipped)` : ''}`);
        onClose();
        if (onSave) await onSave({ date: form.date, project_id: form.project_id, line_id: form.line_id, _skipDefault: true });
      } else if (isSetBased) {
        if (selectedSets.length === 0) { toast.error('Select at least one set'); setLoading(false); return; }
        const res = await axios.post(`${API}/api/plans/create-citybus-sets`, {
          date: form.date, project_id: form.project_id, line_id: form.line_id,
          set_numbers: selectedSets.sort((a, b) => a - b)
        });
        if (res.data?.over_limit?.length > 0) {
          toast.warning(res.data.warning, { duration: 6000 });
        }
        toast.success(`${res.data?.created || selectedSets.length} sets planned${res.data?.skipped?.length ? ` (${res.data.skipped.length} skipped - already planned)` : ''}`);
        onClose();
        if (onSave) await onSave({ date: form.date, project_id: form.project_id, line_id: form.line_id, _skipDefault: true });
      } else {
        // Safety check: if project has hitachi_seat_types but somehow got here, redirect to Hitachi flow
        const proj = projects.find(p => p.id === form.project_id);
        if (proj?.hitachi_seat_types?.length > 0) {
          toast.error('This is a Hitachi project. Please select sets to plan.');
          setLoading(false);
          return;
        }
        await onSave({ date: form.date, project_id: form.project_id, line_id: form.line_id });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create plan');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="create-plan-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-800">Add Project to Day</h3>
            <p className="text-xs text-slate-500">
              {isSetBased ? 'Select which sets to plan for this day' : isTrain ? 'All components will be added as a template' : 'A new plan entry will be created'}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Date</label>
            <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="plan-date-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Project *</label>
            <div className="flex gap-1 mb-2">
              {[{ id: 'all', label: 'All' }, { id: 'train', label: 'Train' }, { id: 'coach', label: 'Coach' }, { id: 'bus', label: 'City Bus' }].map(c => (
                <button key={c.id} type="button" onClick={() => setCatFilter(c.id)}
                  className={`px-2.5 py-1 text-[10px] font-semibold rounded-md transition-all ${catFilter === c.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                  data-testid={`cat-filter-${c.id}`}>{c.label}</button>
              ))}
            </div>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, SR code, customer..."
              className="w-full border rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              data-testid="project-search-input" />
            <div className="max-h-40 overflow-y-auto border rounded-lg divide-y divide-slate-50">
              {filteredProjects.length === 0 && <div className="px-3 py-4 text-center text-xs text-slate-400">No projects found</div>}
              {filteredProjects.map(p => (
                <button key={p.id} type="button" onClick={() => { setForm(f => ({ ...f, project_id: p.id })); setSelectedSets([]); }}
                  className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors flex items-center gap-2 ${form.project_id === p.id ? 'bg-blue-50 ring-1 ring-blue-300' : ''}`}
                  data-testid={`project-option-${p.id}`}>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${p.planning_unit === 'seat' ? 'bg-blue-500' : p.category === 'City Bus' ? 'bg-green-500' : 'bg-orange-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-slate-700 truncate">{p.name}</div>
                    <div className="text-[10px] text-slate-400 flex gap-2">
                      {p.sr_number && <span>{p.sr_number}</span>}
                      <span className={p.category === 'City Bus' ? 'text-green-500' : p.planning_unit === 'seat' ? 'text-blue-400' : 'text-orange-400'}>
                        {p.planning_unit === 'seat' ? 'Train' : (p.category || 'Set')}
                      </span>
                    </div>
                  </div>
                  {form.project_id === p.id && <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Production Line *</label>
            <select value={form.line_id} onChange={e => setForm(f => ({ ...f, line_id: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm mt-1" data-testid="plan-line-select">
              <option value="">-- Select Line --</option>
              {lines.map(l => {
                return (
                  <option key={l.id} value={l.id}>
                    {l.name} ({l.code})
                  </option>
                );
              })}
            </select>
          </div>

          {/* Set Selector for all set-based projects (Coach + City Bus) */}
          {isSetBased && orderedQty > 0 && (
            <div className={`border rounded-lg p-3 ${isHitachi ? 'border-indigo-200 bg-indigo-50/50' : isCityBus ? 'border-green-200 bg-green-50/50' : 'border-orange-200 bg-orange-50/50'}`}>
              <label className={`text-xs font-bold block mb-1 ${isHitachi ? 'text-indigo-700' : isCityBus ? 'text-green-700' : 'text-orange-700'}`}>Set Selection ({selectedSets.length} selected / {orderedQty - preCompletedSets} available)</label>
              {preCompletedSets > 0 && (
                <p className="text-[10px] text-slate-500 mb-1">Sets 1-{preCompletedSets} pre-completed (not selectable)</p>
              )}
              {plannedSetNums.size > 0 && (
                <p className="text-[10px] text-slate-400 mb-2">Planned sets: {formatSetRanges([...plannedSetNums])}</p>
              )}
              {/* Text input for set numbers */}
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={setInput}
                  onChange={e => { setSetInput(e.target.value); setSetInputError(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSetsFromInput(); } }}
                  placeholder="e.g. 1, 3, 5-10, 15"
                  className="flex-1 border rounded px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-green-400"
                  data-testid="set-number-input"
                />
                <button type="button" onClick={addSetsFromInput}
                  className="px-3 py-1.5 text-xs font-semibold bg-green-600 text-white rounded hover:bg-green-700 whitespace-nowrap" data-testid="add-sets-btn">
                  Add
                </button>
              </div>
              {setInputError && (
                <p className="text-[10px] text-amber-600 mb-2" data-testid="set-input-error">{setInputError}</p>
              )}
              {/* Selected sets display */}
              {selectedSets.length > 0 && (
                <div className="bg-white rounded border border-green-100 p-2">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-semibold text-green-700">Selected: {formatSetRanges(selectedSets)}</span>
                    <button type="button" onClick={() => setSelectedSets([])}
                      className="text-[10px] text-red-500 hover:text-red-700 font-medium" data-testid="clear-all-sets">Clear All</button>
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                    {selectedSets.map(num => (
                      <span key={num}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-100 text-green-800 text-[10px] font-medium rounded group cursor-pointer hover:bg-red-100 hover:text-red-700 transition-colors"
                        onClick={() => removeSet(num)}
                        title={`Remove Set #${num}`}
                        data-testid={`selected-set-${num}`}>
                        #{num}
                        <X className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100" />
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {isTrain && !isHitachi && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
              <strong>Train Project:</strong> All components will be added with quantity 0.
            </div>
          )}
          {isHitachi && selectedSets.length > 0 && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-xs text-indigo-700">
              <strong>Hitachi Rail:</strong> {selectedSets.length} set(s) will be created. Each set has {selectedProject?.hitachi_seat_types?.length || 0} seat types to track.
              {preCompletedSets > 0 && <span className="block mt-1 text-[10px] text-slate-500">Sets 1-{preCompletedSets} are pre-completed and not available for planning.</span>}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
            <button type="submit" disabled={loading || !form.project_id || (isSetBased && selectedSets.length === 0)}
              className="px-4 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-900 disabled:opacity-50" data-testid="plan-save-btn">
              {loading ? 'Creating...' : isHitachi ? `Plan ${selectedSets.length} Hitachi Sets` : isSetBased ? `Plan ${selectedSets.length} Sets` : 'Add to Day'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
